// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// V12 — verifier-side crypto primitives for stateless ownership
// signing.
//
// This file is a *narrow subset* of user-app's `web/key-agent/src/crypto.ts`.
// What's kept:
//   - BIP-39 mnemonic validation
//   - BIP-32/44 multisig-slot derivation  (m/84'/0'/{account}'/0/0),
//     from a mnemonic (recovery path) or a raw master seed (Web3Auth path)
//   - web3authKeyToSeed — normalise a Web3Auth-reconstructed secp256k1
//     key into the 32-byte BIP-32 master seed (Web3Auth WaaS ownership
//     signing); byte-for-byte identical to user-app's web3auth-agent
//   - signOwnershipMessage — the canonical V06 ownership-challenge
//     signature primitive
//   - OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR — MUST match
//     `services/verifier/src/ownership.rs::CHALLENGE_DOMAIN_SEPARATOR`
//
// Deliberately absent from the verifier build:
//   - createMnemonic, getRefundKey, getIdentityKey, getWalletKey
//   - signChallengeMessage (SIWB login — unused on verifier)
//   - signPsbtWithKey (withdrawals — verifier never signs PSBTs)
//   - deriveP2wpkhAddress + any btc-signer dep (no addresses to derive)
//   - Anything involving networks (no chain interaction)
//
// A compromised verifier origin that somehow exfiltrated a seed could
// still run BIP-32 derivation on its own, so trimming this surface is
// code-hygiene rather than defence-in-depth. The defence is V13's
// statelessness — the seed is never persisted in the verifier origin.
//

import { mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { HDKey } from '@scure/bip32';
import { secp256k1 } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';

/**
 * Multisig-slot derivation path for a vault. Must match user-app's
 * key-agent byte-for-byte — the whole premise of this package is that
 * the verifier derives to the same pubkey user-app does.
 *
 *   U (multisig key):  m/84'/0'/{account}'/0/0
 */
function multisigPath(accountIndex: number): string {
  return `m/84'/0'/${accountIndex}'/0/0`;
}

/** Validate a BIP39 mnemonic string against the English wordlist. */
export function isValidMnemonic(mnemonic: string): boolean {
  return validateMnemonic(mnemonic, wordlist);
}

/**
 * Derive an HD key from a raw BIP-32 master seed. Abstracting the seed
 * source lets the same derivation tree serve both the recovery path
 * (seed = BIP-39 mnemonic → 64-byte seed) and the Web3Auth WaaS path
 * (seed = the reconstructed secp256k1 key, used directly as 32 bytes of
 * seed entropy). Byte-for-byte identical to user-app's
 * `deriveKeyFromSeed` — the whole premise of this package is that the
 * verifier derives to the same pubkey user-app does. The caller owns
 * `seed` and must zeroise it.
 */
function deriveKeyFromSeed(seed: Uint8Array, path: string): HDKey {
  const master = HDKey.fromMasterSeed(seed);
  return master.derive(path);
}

function deriveKey(mnemonic: string, path: string): HDKey {
  const seed = mnemonicToSeedSync(mnemonic);
  try {
    return deriveKeyFromSeed(seed, path);
  } finally {
    seed.fill(0);
  }
}

export interface DerivedKey {
  pubkey: Uint8Array;
  privateKey: Uint8Array;
}

/** Derive the multisig key (U) for a vault at the given account index.
 *  The returned `privateKey` is a caller-owned `Uint8Array`; callers
 *  must zero it with `.fill(0)` after use. */
export function getMultisigKey(mnemonic: string, accountIndex: number): DerivedKey {
  const key = deriveKey(mnemonic, multisigPath(accountIndex));
  if (!key.publicKey || !key.privateKey) {
    throw new Error('Multisig key derivation failed');
  }
  return { pubkey: key.publicKey, privateKey: key.privateKey };
}

/** Derive the multisig key (U) from a raw BIP-32 master seed (see
 *  [`deriveKeyFromSeed`]). Used by the Web3Auth WaaS ownership-proof
 *  path, where the seed is the reconstructed secp256k1 key rather than a
 *  mnemonic. The returned `privateKey` is a caller-owned `Uint8Array`;
 *  callers must zero it with `.fill(0)` after use. */
export function getMultisigKeyFromSeed(seed: Uint8Array, accountIndex: number): DerivedKey {
  const key = deriveKeyFromSeed(seed, multisigPath(accountIndex));
  if (!key.publicKey || !key.privateKey) {
    throw new Error('Multisig key derivation failed');
  }
  return { pubkey: key.publicKey, privateKey: key.privateKey };
}

/**
 * Normalise a Web3Auth-provided secp256k1 private key (hex) into the
 * 32-byte buffer used as a BIP-32 master seed. Accepts an optional `0x`
 * prefix and any case; rejects anything that is not exactly 32 bytes.
 *
 * Copied verbatim from user-app's `web3auth-agent.ts::web3authKeyToSeed`
 * (per "code quality rules are copied, not shared") so the verifier
 * derives to the identical pubkey for the same Web3Auth account — the
 * key the user's vault descriptor was created with under TechCo's
 * Web3Auth clientId.
 */
export function web3authKeyToSeed(privateKeyHex: string): Uint8Array {
  const clean = privateKeyHex.trim().toLowerCase().replace(/^0x/, '');
  if (!/^[0-9a-f]{64}$/.test(clean)) {
    throw new Error('Web3Auth private key must be 32 bytes (64 hex chars)');
  }
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Encode a compressed public key as lowercase hex. Used by tests to
 *  assert parity against user-app's derived pubkeys. */
export function pubkeyToHex(pubkey: Uint8Array): string {
  return Array.from(pubkey)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * V06: domain-separator prefix the verifier expects on the canonical
 * ownership-proof challenge message. MUST be kept in lock-step with
 * `services/verifier/src/ownership.rs::CHALLENGE_DOMAIN_SEPARATOR`
 * and with user-app's `OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR`. Distinct
 * from the SIWB login prefix so signatures cannot cross-replay.
 */
export const OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR =
  'cobault-verifier-ownership-v1:';

/**
 * HW.12: build the canonical verifier ownership-proof challenge — the exact
 * UTF-8 string bound into the proof. Both the `der-sha256-v1` software path
 * ([`signOwnershipMessage`]) and the hardware `bsm-bip137-v1` path (the native
 * Ledger arm, which BSM-signs this string on-device — see `device.ts`) consume
 * this one source, so the signed message can never drift between backends.
 * MUST reproduce user-app's `ownershipChallengeMessage` and the verifier's
 * `ownership.rs::canonical_ownership_message` byte-for-byte.
 *
 * Format: the domain separator, the UUID in canonical form, a colon, then the
 * 64-char lowercase hex nonce supplied by the verifier.
 */
export function ownershipChallengeMessage(
  vaultId: string,
  nonceHex: string,
): string {
  if (!/^[0-9a-f]{64}$/.test(nonceHex)) {
    throw new Error('nonceHex must be 64 lowercase hex characters');
  }
  return `${OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR}${vaultId}:${nonceHex}`;
}

/**
 * COB-507: domain-separator prefix for the **shared-vault** review site
 * (`verifier-shared`). MUST be kept in lock-step with
 * `services/verifier-shared/src/ownership.rs::CHALLENGE_DOMAIN_SEPARATOR`.
 *
 * Deliberately distinct from [`OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR`] (the
 * yield surface): the two verifiers are separate services with separate
 * identities (R-INV-7), so a signature harvested on one site must be inert on
 * the other. The `-shared-` infix is what enforces that.
 */
export const SHARED_OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR =
  'cobault-verifier-shared-ownership-v1:';

/**
 * COB-507: build the canonical **shared-vault** participant-proof challenge —
 * the exact UTF-8 string bound into the proof posted to verifier-shared's
 * `POST /lookup/{id}/prove`. MUST reproduce
 * `verifier-shared/src/ownership.rs::canonical_ownership_message` byte-for-byte
 * (golden vector site `verifier-shared-ownership`).
 *
 * Format: the shared domain separator, the UUID in canonical form, a colon,
 * then the 64-char lowercase hex nonce supplied by the verifier.
 */
export function sharedOwnershipChallengeMessage(
  vaultId: string,
  nonceHex: string,
): string {
  if (!/^[0-9a-f]{64}$/.test(nonceHex)) {
    throw new Error('nonceHex must be 64 lowercase hex characters');
  }
  return `${SHARED_OWNERSHIP_CHALLENGE_DOMAIN_SEPARATOR}${vaultId}:${nonceHex}`;
}

/**
 * COB-507: sign a shared-vault participant-proof challenge. Identical crypto to
 * [`signOwnershipMessage`] — `SHA-256(message)` signed with secp256k1 ECDSA,
 * RFC6979-deterministic, low-S (`@noble/curves` default) — over the SHARED
 * canonical message. Returns the DER-encoded signature as lowercase hex, which
 * the caller wraps into a WS-H `der-sha256-v1` proof object.
 */
export function signSharedOwnershipMessage(
  privateKey: Uint8Array,
  vaultId: string,
  nonceHex: string,
): string {
  const message = sharedOwnershipChallengeMessage(vaultId, nonceHex);
  const msgHash = sha256(new TextEncoder().encode(message));
  const sig = secp256k1.sign(msgHash, privateKey);
  return sig.toDERHex();
}

/**
 * Sign a verifier ownership-proof challenge. The message format is:
 *
 *   SHA-256("cobault-verifier-ownership-v1:<vault_id>:<nonce_hex>")
 *
 * — i.e. the domain separator, the UUID in canonical form, a colon,
 * then the 64-char lowercase hex nonce from the verifier's
 * `/lookup/{id}/challenge` endpoint.
 *
 * Returns the DER-encoded secp256k1 ECDSA signature as lowercase hex.
 * RFC6979-deterministic under `@noble/curves/secp256k1`, so a given
 * (privateKey, vaultId, nonceHex) always produces the same signature.
 */
export function signOwnershipMessage(
  privateKey: Uint8Array,
  vaultId: string,
  nonceHex: string,
): string {
  const message = ownershipChallengeMessage(vaultId, nonceHex);
  const msgHash = sha256(new TextEncoder().encode(message));
  const sig = secp256k1.sign(msgHash, privateKey);
  return sig.toDERHex();
}
